/**
 * Runtime dataset LOADER (browser-safe). Reads the generated Gen 1-3 dataset that
 * the one-time ingest wrote to the public dir (served by Vite at
 * `/generated/dataset.json`). NEVER calls PokeAPI — the JSON is fully local.
 *
 * If the generated dataset is absent (ingest not yet run, or unit-test context),
 * callers fall back to the hermetic fixtures so tests stay deterministic.
 */
import type { SpeciesData, MoveData } from '@/shared';
import type { GameDataset, DataIndex, RenderManifest, RenderSpriteMeta } from './schema.js';
import { buildDataIndex } from './index.js';

/** Public URL Vite serves the generated dataset from (see ingest publicDir mirror). */
export const GENERATED_DATASET_URL = '/generated/dataset.json';

/** Public URL Vite serves the standalone 3D-render manifest from. */
export const GENERATED_RENDERS_URL = '/generated/renders.json';

let cached: GameDataset | null = null;

/**
 * Fetch + cache the generated dataset. Returns null (never throws) when the file
 * is missing or malformed, so the caller can fall back to fixtures.
 */
export async function loadGeneratedDataset(
  url: string = GENERATED_DATASET_URL,
): Promise<GameDataset | null> {
  if (cached) return cached;
  if (typeof fetch !== 'function') return null;
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const data = (await res.json()) as GameDataset;
    if (!Array.isArray(data.species) || !Array.isArray(data.moves)) return null;
    cached = data;
    return data;
  } catch {
    return null;
  }
}

/**
 * Resolve the per-dex 3D-model RENDER metadata (front/back animated spritesheets) the
 * runtime prefers for battles + the dex. Reads it from the bundled dataset's `renders`
 * field when present, else fetches the standalone renders.json. Returns an empty map
 * (never throws) when neither exists — the SpriteResolver then falls back to the Gen5
 * sprite chain per side, so a missing manifest is graceful, never a crash.
 */
export async function loadRenderIndex(
  datasetUrl?: string,
  rendersUrl: string = GENERATED_RENDERS_URL,
): Promise<Readonly<Record<number, RenderSpriteMeta>>> {
  const dataset = await loadGeneratedDataset(datasetUrl);
  if (dataset?.renders && Object.keys(dataset.renders).length > 0) return dataset.renders;
  if (typeof fetch !== 'function') return {};
  try {
    const res = await fetch(rendersUrl);
    if (!res.ok) return {};
    const manifest = (await res.json()) as RenderManifest;
    return manifest.byDex ?? {};
  } catch {
    return {};
  }
}

/** Build the runtime lookup index from the generated dataset, else from fixtures. */
export async function loadDataIndex(url?: string): Promise<DataIndex> {
  const dataset = await loadGeneratedDataset(url);
  if (dataset) return buildDataIndex(dataset.species, dataset.moves);
  return buildDataIndex(); // fixtures fallback
}

/** Resolve the species + move arrays the runtime should use (generated or fixtures). */
export async function loadDataset(url?: string): Promise<{
  species: readonly SpeciesData[];
  moves: readonly MoveData[];
  fromGenerated: boolean;
}> {
  const dataset = await loadGeneratedDataset(url);
  if (dataset) {
    return { species: dataset.species, moves: dataset.moves, fromGenerated: true };
  }
  const { FIXTURE_SPECIES, FIXTURE_MOVES } = await import('./fixtures.js');
  return { species: FIXTURE_SPECIES, moves: FIXTURE_MOVES, fromGenerated: false };
}

/** Test/HMR seam: drop the in-memory dataset cache. */
export function _resetGeneratedDatasetCache(): void {
  cached = null;
}
